import React, { useState } from 'react';
import { Group, Text, NumberInput, SegmentedControl, ActionIcon, Tooltip, MultiSelect, Select, SelectProps } from '@mantine/core';
import { IconSettings, IconBan, IconTrash } from '@tabler/icons-react';

export type PoolTypeProviderMode = 'counter' | 'percent';

export interface PoolTypeProviderValuesRange {
  min?: number | null;
  max?: number | null;
}
export interface PoolTypeProviderRangeState {
  mode: PoolTypeProviderMode;
  values: Record<string, PoolTypeProviderValuesRange>;
}

const PROVIDERS = [
  { key: 'CAMM', label: 'RAY|CAMM' },
  { key: 'CPMM', label: 'RAY|CPMM' },
  { key: 'DLMM', label: 'Met|DLMM' },
  { key: 'DBC', label: 'Met|DBC' },
  { key: 'DAMM_V1', label: 'Met|DAMM_V1' },
  { key: 'DAMM_V2', label: 'Met|DAMM_V2' },
  { key: 'NATIVE', label: 'PF|NATIVE' },
  { key: 'SWAPS', label: 'PF|SWAPS' },
  { key: 'Launchpad', label: 'RAY|Launchpad' },
  { key: 'AMM_v4', label: 'RAY|AMM_v4' },
];

const DEFAULT_VALUES: PoolTypeProviderRangeState = {
  mode: 'counter',
  values: {
    CPMM: { min: 10, max: 30 }
  }
};

interface Props {
  value?: PoolTypeProviderRangeState;
  onChange: (val: PoolTypeProviderRangeState | undefined) => void;
}

// Helper to render SVG icon with graceful fallback (similar to PoolProvidersFilter)
const IconCell: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
  const [err, setErr] = useState(false);
  if (err) return null;
  return (
    <img
      src={src}
      alt={alt}
      width={18}
      height={18}
      style={{ display: 'block' }}
      onError={() => setErr(true)}
    />
  );
};

const getPoolTypeIconSrc = (key: string) => {
  const base = process.env.PUBLIC_URL || '';
  const map: Record<string, string> = {
    CAMM: `${base}/icons/dex/raydium.svg`,
    CPMM: `${base}/icons/dex/raydium.svg`,
    Launchpad: `${base}/icons/dex/raydium.svg`,
    AMM_v4: `${base}/icons/dex/raydium.svg`,
    DLMM: `${base}/icons/dex/meteora.svg`,
    DBC: `${base}/icons/dex/meteora.svg`,
    DAMM_V1: `${base}/icons/dex/meteora.svg`,
    DAMM_V2: `${base}/icons/dex/meteora.svg`,
    NATIVE: `${base}/icons/dex/PumpFun.svg`,
    SWAPS: `${base}/icons/dex/PumpFun.svg`,
  };
  return map[key] || '';
};

// Кастомный рендерер для опций с иконкой (Mantine v7)
const renderOption: SelectProps['renderOption'] = ({ option, checked }) => (
  <Group gap={8} align="center" style={{ width: '100%' }}>
    <IconCell src={getPoolTypeIconSrc(option.value)} alt={option.label} />
    <Text size="sm">{option.label}</Text>
    {checked && <span style={{ marginLeft: 'auto', color: '#4F46E5' }}>✓</span>}
  </Group>
);

const PoolTypeProvidersFilter: React.FC<Props> = ({ value, onChange }) => {
  const mode = value?.mode || 'counter';
  const values = value?.values || {};

  // Список выбранных провайдеров
  const selectedKeys = Object.keys(values);

  // Обработка выбора провайдера через Select (только один)
  const handleProviderChange = (key: string | null) => {
    if (!key) return;
    // Если уже выбран, ничего не делаем
    if (selectedKeys.includes(key)) return;
    onChange({
      mode,
      values: {
        ...values,
        [key]: { min: undefined, max: undefined }
      }
    });
  };

  const handleMinChange = (key: string, min: number | '' | null) => {
    onChange({
      mode,
      values: {
        ...values,
        [key]: { ...values[key], min: min === '' ? undefined : min }
      }
    });
  };

  const handleMaxChange = (key: string, max: number | '' | null) => {
    onChange({
      mode,
      values: {
        ...values,
        [key]: { ...values[key], max: max === '' ? undefined : max }
      }
    });
  };

  const handleModeChange = (newMode: PoolTypeProviderMode) => {
    onChange({ mode: newMode, values });
  };

  const handleDefault = () => {
    onChange({
      mode: 'counter',
      values: {
        CPMM: { min: 10, max: 30 }
      }
    });
  };

  const handleClear = () => {
    onChange(undefined);
  };

  // Валидация: сумма min для percent
  const totalMinPercent = mode === 'percent'
    ? Object.values(values).reduce((acc, v) => acc + (typeof v.min === 'number' ? v.min : 0), 0)
    : 0;
  const percentSumError = mode === 'percent' && totalMinPercent > 100;

  // Валидация: min > max для каждого поля
  const minMaxErrors: Record<string, boolean> = {};
  selectedKeys.forEach(key => {
    const v = values[key];
    if (
      typeof v.min === 'number' &&
      typeof v.max === 'number' &&
      v.min > v.max
    ) {
      minMaxErrors[key] = true;
    }
  });

  return (
    <div style={{ marginBottom: 16 }}>
      <Group justify="space-between" mb={4}>
        <Text fw={500} size="sm">Pool Type</Text>
        <Group gap={4}>
          <Tooltip label="Default">
            <ActionIcon size="xs" variant="subtle" onClick={handleDefault}>
              <IconSettings size={14} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Clear">
            <ActionIcon size="xs" variant="subtle" color="red" onClick={handleClear}>
              <IconBan size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
      {/* --- SegmentedControl и Select в одной линии --- */}
      <Group mb={8} gap={8} align="center" wrap="nowrap">
        <Select
          data={PROVIDERS.map(p => ({ value: p.key, label: p.label }))}
          onChange={handleProviderChange}
          placeholder="Select pool type..."
          size="xs"
          value={null}
          nothingFoundMessage="No pool types"
          renderOption={renderOption}
          styles={{
              input: {
                backgroundColor: '#21262D',
                border: '1px solid #374151',
                color: '#F9FAFB',
                minHeight: '28px',
                fontSize: '13px',
                width: 160,
                maxWidth: 180,
                textAlign: 'left',
              },
              dropdown: {
                backgroundColor: '#181C23',
                border: '1px solid #23272F'
              },
              option: {
                backgroundColor: '#181C23',
                color: '#F9FAFB',
                '&[dataSelected]': {
                  backgroundColor: '#4F46E5'
                },
                '&:hover': {
                  backgroundColor: '#21262D'
                }
              }
            }}
        />
        <SegmentedControl
          value={mode}
          onChange={v => handleModeChange(v as PoolTypeProviderMode)}
          data={[
            { label: 'count', value: 'counter' },
            { label: 'percent', value: 'percent' }
          ]}
          size="xs"
          style={{ minWidth: 120, flex: 1 }}
        />
      </Group>
      {/* --- конец блока --- */}
      {percentSumError && (
        <Text size="xs" c="red" mb={4}>
          Total percent min cannot exceed 100% (now: {totalMinPercent}%)
        </Text>
      )}
      {selectedKeys.length > 0 && (
        <div>
          {selectedKeys.map(key => {
            const label = PROVIDERS.find(p => p.key === key)?.label || key;
            const val = values[key] || {};
            const hasMinMaxError = !!minMaxErrors[key];
            return (
              <Group key={key} gap={6} mb={6} align="center" wrap="nowrap">
                {/* SVG icon for pool type provider (platform-based) */}
                <span style={{ display: 'flex', alignItems: 'center', minWidth: 22 }}>
                  <IconCell src={getPoolTypeIconSrc(key)} alt={key} />
                </span>
                <Text size="xs" style={{ minWidth: 110, textAlign: 'left' }}>{label}</Text>
                <NumberInput
                  placeholder="Min"
                  value={val.min ?? ''}
                  max={mode === 'percent' ? 100 : undefined}
                  onChange={v => {
                    let parsed: number | '' | null;
                    if (typeof v === 'string') {
                      parsed = v === '' ? '' : Number(v);
                      if (isNaN(parsed as number)) parsed = '';
                    } else {
                      parsed = v;
                    }
                    handleMinChange(key, parsed);
                  }}
                  size="xs"
                  error={hasMinMaxError ? 'Min > Max' : undefined}
                  styles={{
                    input: {
                      backgroundColor: '#181B20',
                      border: '1px solid #23272F',
                      color: '#F9FAFB',
                      borderRadius: 12,
                      fontSize: 14,
                      minHeight: 32,
                      paddingLeft: 12,
                      paddingRight: 12,
                      textAlign: 'center',
                    }
                  }}
                  rightSection={mode === 'percent' ? <span style={{ color: '#aaa', fontSize: 12 }}>%</span> : undefined}
                  hideControls
                />
                <NumberInput
                  placeholder="Max"
                  value={val.max ?? ''}
                  max={mode === 'percent' ? 100 : undefined}
                  onChange={v => {
                    let parsed: number | '' | null;
                    if (typeof v === 'string') {
                      parsed = v === '' ? '' : Number(v);
                      if (isNaN(parsed as number)) parsed = '';
                    } else {
                      parsed = v;
                    }
                    handleMaxChange(key, parsed);
                  }}
                  size="xs"
                  error={hasMinMaxError ? 'Min > Max' : undefined}
                  styles={{
                    input: {
                      backgroundColor: '#181B20',
                      border: '1px solid #23272F',
                      color: '#F9FAFB',
                      borderRadius: 12,
                      fontSize: 14,
                      minHeight: 32,
                      paddingLeft: 12,
                      paddingRight: 12,
                      textAlign: 'center',
                    }
                  }}
                  rightSection={mode === 'percent' ? <span style={{ color: '#aaa', fontSize: 12 }}>%</span> : undefined}
                  hideControls
                />
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  color="red"
                  onClick={() => {
                    const next = selectedKeys.filter(k => k !== key);
                    // Удаляем из values
                    const newValues: Record<string, PoolTypeProviderValuesRange> = {};
                    next.forEach(k => { newValues[k] = values[k]; });
                    onChange({ mode, values: newValues });
                  }}
                  title="Delete"
                >
                  <IconTrash size={14} />
                </ActionIcon>
              </Group>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PoolTypeProvidersFilter;
